import React, { useState } from 'react';
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Pagination,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, HistoryIcon, SyncAltIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useWalletTransactions } from '../../../hooks/useWalletTransactions';
import { formatPrice } from '../../../utils/money';
import { formatDateTime } from '../../../utils/dateFormat';
import { SignedAmount, TransactionKindLabel } from './paymentDisplay';

/**
 * "Statement" tab of Organization › Billing — the wallet ledger, newest first, paginated
 * server-side (`GET /clients/wallet/transactions?page&pageSize`, docs/api-types/wallet.md).
 *
 * There is deliberately NO search field: the contract exposes only `page`/`pageSize` for this
 * endpoint (no `q`), and mixing a client-side filter over one server page is the anti-pattern the
 * project rules forbid. Add a SearchInput only once the backend grows a search parameter.
 */
const WalletStatementTab: React.FunctionComponent = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const { transactions, pagination, loading, error, refetch } = useWalletTransactions({ page, pageSize: perPage });

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };
  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  // Initial load only — a page change keeps the table mounted instead of flashing a spinner.
  if (loading && pagination === null && !error) {
    return <CenteredSpinner />;
  }

  if (error) {
    return (
      <EmptyState
        titleText="Unable to load your statement"
        icon={ExclamationCircleIcon}
        variant="lg"
        headingLevel="h2"
        status="danger"
      >
        <EmptyStateBody>{error}</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={() => void refetch()}>
              Retry
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  if (!loading && (pagination?.total ?? 0) === 0) {
    return (
      <EmptyState titleText="No movements yet" icon={HistoryIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>
          Your prepaid balance has not moved yet. Top-ups, purchases paid from the balance and any corrections will be
          listed here.
        </EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="secondary" icon={<SyncAltIcon />} onClick={() => void refetch()}>
              Refresh
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  }

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <Button
              variant="secondary"
              icon={<SyncAltIcon />}
              onClick={() => void refetch()}
              isLoading={loading}
              isDisabled={loading}
              aria-label="Refresh statement"
            >
              Refresh
            </Button>
          </ToolbarItem>
          <ToolbarItem variant="pagination">
            <Pagination
              itemCount={pagination?.total ?? 0}
              perPage={perPage}
              page={page}
              onSetPage={onSetPage}
              onPerPageSelect={onPerPageSelect}
            />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      <Table aria-label="Prepaid balance statement" variant="compact" borders={false}>
        <Thead>
          <Tr>
            <Th>Date</Th>
            <Th>Kind</Th>
            <Th>Description</Th>
            <Th>Reference</Th>
            <Th className="pf-v6-u-text-align-end">Amount</Th>
            <Th className="pf-v6-u-text-align-end">Balance after</Th>
          </Tr>
        </Thead>
        <Tbody>
          {transactions.map((tx) => (
            <Tr key={tx.id}>
              <Td dataLabel="Date">{formatDateTime(tx.created_at)}</Td>
              <Td dataLabel="Kind">
                <TransactionKindLabel kind={tx.kind} amount={tx.amount} />
              </Td>
              <Td dataLabel="Description">{tx.description}</Td>
              <Td dataLabel="Reference">{tx.reference_label ?? '—'}</Td>
              <Td dataLabel="Amount" className="pf-v6-u-text-align-end">
                <SignedAmount value={tx.amount} />
              </Td>
              <Td dataLabel="Balance after" className="pf-v6-u-text-align-end">
                {formatPrice(tx.balance_after)}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <Pagination
        itemCount={pagination?.total ?? 0}
        perPage={perPage}
        page={page}
        onSetPage={onSetPage}
        onPerPageSelect={onPerPageSelect}
        variant="bottom"
      />
    </>
  );
};

export default WalletStatementTab;
